import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from 'react-i18next';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

/**
 * ProcessSection component for the homepage
 * Displays the translation process steps
 * Designed with an elegant, minimalistic black and white approach
 * Supports multiple languages through i18n
 */
const ProcessSection = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  
  const steps = [
    {
      number: t('process.step1.number'),
      title: t('process.step1.title'),
      description: t('process.step1.description')
    },
    {
      number: t('process.step2.number'),
      title: t('process.step2.title'),
      description: t('process.step2.description')
    },
    {
      number: t('process.step3.number'),
      title: t('process.step3.title'),
      description: t('process.step3.description')
    },
    {
      number: t('process.step4.number'),
      title: t('process.step4.title'),
      description: t('process.step4.description')
    }
  ];

  // Animation variants
  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  return (
    <section ref={sectionRef} className="py-32 bg-white scroll-section relative">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-1/4 h-px bg-primary/10"></div>
      <div className="absolute top-40 right-0 w-1/3 h-px bg-primary/5"></div>
      
      <div className="container mx-auto px-6">
        <motion.div 
          className="max-w-3xl mx-auto mb-24 text-center"
          variants={titleVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <div className="w-16 h-px bg-primary mx-auto mb-12"></div>
          <h2 className="font-heading text-3xl md:text-5xl font-normal mb-8 tracking-wide">
            {t('process.title')}
          </h2>
          <p className="text-light text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto">
            {t('process.subtitle')}
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto relative">
          {/* Vertical line connecting steps */}
          <div className="absolute left-[40px] md:left-[60px] top-0 bottom-0 w-px bg-primary/10 z-0"></div>
          
          {steps.map((step, index) => (
            <ProcessStep 
              key={index}
              number={step.number}
              title={step.title}
              description={step.description}
              isLast={index === steps.length - 1}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * ProcessStep component
 * Individual step in the translation process
 */
const ProcessStep = ({ number, title, description, isLast, index }) => {
  const stepRef = useRef(null);
  const isInView = useInView(stepRef, { once: true, amount: 0.5 });

  // Animation variants
  const stepVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { 
        duration: 0.8,
        delay: index * 0.2,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  return (
    <motion.div 
      ref={stepRef}
      variants={stepVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-24 last:mb-0 relative z-10"
    >
      {/* Step number in circle */}
      <div className="md:col-span-2 flex items-start">
        <div className="w-20 h-20 rounded-full border border-primary/20 flex items-center justify-center bg-white z-10">
          <span className="text-primary font-light text-lg tracking-widest">{number}</span>
        </div>
      </div>
      
      {/* Step content */}
      <div className="md:col-span-3 pt-2">
        <h3 className="text-2xl font-heading mb-0 tracking-wide">{title}</h3>
      </div>
      
      {/* Step description */}
      <div className="md:col-span-7 pt-2">
        <p className="text-light font-light text-lg">{description}</p>
        {!isLast && <div className="w-full h-px bg-border mt-16 md:hidden"></div>}
      </div>
    </motion.div>
  );
};

export default ProcessSection;
